import { useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Reads the tenant's whitelabel config (or brand_kit fallback) once after auth
 * and applies it to the *whole dashboard chrome* via CSS variables + a single
 * dynamic <style> tag. Zero re-renders after mount — pure CSS overrides.
 *
 * Skinned surfaces:
 *   • CSS vars  (--brand-primary / --brand-secondary / --brand-accent)
 *   • All `.bg-emerald-brand`, `.text-emerald-brand`, `.border-emerald-brand`
 *   • Sidebar header logo (replaced via global event)
 *
 * Renders nothing.
 */
const STYLE_ID = '__konnect_chrome_brand_style';

function hexToRgb(hex) {
  if (!hex) return null;
  const h = hex.replace('#', '');
  if (h.length !== 6 && h.length !== 3) return null;
  const f = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = parseInt(f, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

function darken([r, g, b], amount = 0.12) {
  const m = (v) => Math.max(0, Math.round(v * (1 - amount)));
  return `rgb(${m(r)}, ${m(g)}, ${m(b)})`;
}

function applyBrand(brand) {
  if (!brand) return;
  const root = document.documentElement;
  const primary = brand.primary_color;
  const secondary = brand.secondary_color;
  const accent = brand.accent_color;
  if (primary) root.style.setProperty('--brand-primary', primary);
  if (secondary) root.style.setProperty('--brand-secondary', secondary);
  if (accent) root.style.setProperty('--brand-accent', accent);

  const rgb = primary && hexToRgb(primary);
  const hover = rgb ? darken(rgb, 0.10) : null;

  let style = document.getElementById(STYLE_ID);
  if (!style) {
    style = document.createElement('style');
    style.id = STYLE_ID;
    document.head.appendChild(style);
  }
  // Override the most common emerald-brand utility classes used across the app.
  // Tailwind classes that look like `bg-emerald-brand` are referenced literally
  // throughout — overriding them here is the lightest-touch way to re-skin.
  style.textContent = primary ? `
    .bg-emerald-brand          { background-color: ${primary} !important; }
    .hover\\:bg-emerald-hover:hover,
    .bg-emerald-hover          { background-color: ${hover || primary} !important; }
    .text-emerald-brand        { color: ${primary} !important; }
    .border-emerald-brand      { border-color: ${primary} !important; }
    .ring-emerald-brand        { --tw-ring-color: ${primary} !important; }
    .from-emerald-brand        { --tw-gradient-from: ${primary} !important; }
    .to-emerald-brand          { --tw-gradient-to: ${primary} !important; }
  ` : '';

  if (brand.logo_url) {
    // Sidebar reads from window.__KONNECT_BRAND_LOGO if present
    window.__KONNECT_BRAND_LOGO = brand.logo_url;
    window.dispatchEvent(new CustomEvent('konnect:brand-logo', { detail: brand.logo_url }));
  }
  if (brand.company_name) {
    window.__KONNECT_BRAND_NAME = brand.company_name;
  }
  // Swap favicon to operator's logo (or favicon_url if explicitly set).
  // Browsers cache favicons aggressively; appending the URL itself as part of
  // the href is enough — we replace the existing <link> entirely.
  const favicon = brand.favicon_url || brand.logo_url;
  if (favicon) {
    document.querySelectorAll(
      "link[rel='icon'], link[rel='shortcut icon'], link[rel='apple-touch-icon']",
    ).forEach((el) => el.parentNode.removeChild(el));
    const link = document.createElement('link');
    link.rel = 'icon';
    link.href = favicon;
    document.head.appendChild(link);
    const apple = document.createElement('link');
    apple.rel = 'apple-touch-icon';
    apple.href = favicon;
    document.head.appendChild(apple);
  }
  document.title = brand.company_name ? `${brand.company_name} · Konnect` : document.title;
}

export default function ChromeBrandProvider() {
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    // 1. Try whitelabel/me first (richer data: logo, headings, footer)
    axios.get(`${API}/api/whitelabel/me`).then((r) => {
      if (cancelled) return;
      const cfg = r.data?.config;
      if (cfg && (cfg.primary_color || cfg.logo_url)) {
        applyBrand(cfg);
        return;
      }
      // 2. Fallback to settings/brand-kit (no logo there yet, but colors)
      axios.get(`${API}/api/settings/brand-kit`).then((r2) => {
        if (cancelled) return;
        applyBrand(r2.data || {});
      }).catch(() => { /* silent */ });
    }).catch(() => {
      axios.get(`${API}/api/settings/brand-kit`).then((r2) => {
        if (cancelled) return;
        applyBrand(r2.data || {});
      }).catch(() => { /* silent */ });
    });

    return () => { cancelled = true; };
  }, [user]);

  return null;
}
